import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { repositoryFactory, DataSourceStrategy, CacheStrategy } from '@rammetec/data-access'
import { VH_USER_ACCESS, type VhUserAccess } from '@vectorhub/domain'
import { ModulesProvider, useCurrentUser, useSessionContext } from '@vectorhub/application'
import { useOrgVectorhubAllowedModules } from '@/shared/hooks/useOrgVectorhubAllowedModules'

function useVhUserModules(orgId: string | null, uid: string | null): {
  userModules: string[]
  isLoading: boolean
} {
  const { data, isLoading } = useQuery<string[]>({
    queryKey: ['vh-user-access', orgId, uid],
    queryFn: async () => {
      const repo = await repositoryFactory.getRepository<VhUserAccess>(
        `org/${orgId!}/vh_user_access`,
        VH_USER_ACCESS,
        DataSourceStrategy.Firestore,
        CacheStrategy.InMemory,
      )
      const doc = await repo.getById(uid!)
      return doc?.modules ?? []
    },
    enabled: !!orgId && !!uid,
    staleTime: 5 * 60 * 1000,
  })
  return { userModules: data ?? [], isLoading: !!orgId && !!uid && isLoading }
}

/**
 * Reads the org's contracted VH modules and the user's granted VH modules from Firestore,
 * then feeds them to ModulesProvider (which calls resolveVectorhubModules to compute the
 * intersection). Lives in the console so @vectorhub/application stays UI-agnostic.
 */
export function ModuleResolverBridge({ children }: { children: ReactNode }) {
  const { user } = useCurrentUser()
  const { currentOrgId } = useSessionContext()

  const uid = user?.userId ?? null

  const { allowedModules } = useOrgVectorhubAllowedModules(currentOrgId)
  const { userModules } = useVhUserModules(currentOrgId, uid)

  return (
    <ModulesProvider orgAllowedModules={allowedModules} userModules={userModules}>
      {children}
    </ModulesProvider>
  )
}
